// Links inside content HTML (Markdown rendered with dangerouslySetInnerHTML)
// are plain <a> tags, which the router doesn't see: clicking one to a page on
// this site would reload the whole app. Containers of content HTML carry
// `data-content` (CONTENT_ATTRIBUTE), and one delegated click handler
// (`installContentLinks`, from the root route) sends their links to our own
// pages through the router instead, as `<Link>` would. Redirected paths are
// handled by the root route's beforeLoad and unprerendered ones by the 404
// page, same as any navigation. Router `<Link>`s call preventDefault, so
// they're skipped.
import type { AnyRouter } from '@tanstack/react-router'

/** Spread onto an element whose children are content HTML. */
export const CONTENT_ATTRIBUTE = { 'data-content': '' }

type Click = {
  button: number
  metaKey: boolean
  ctrlKey: boolean
  shiftKey: boolean
  altKey: boolean
  defaultPrevented: boolean
}

type Anchor = { href: string | null; target: string | null; download: boolean }

/**
 * The href to navigate to in-app, or undefined to leave the click to the
 * browser: other sites, `mailto:`, `#anchors`, files (`/uploads/x.pdf`),
 * new-tab links and modified clicks (cmd/ctrl/shift/alt, middle button).
 */
export function routableHref(click: Click, a: Anchor): string | undefined {
  if (click.defaultPrevented || click.button !== 0) return undefined
  if (click.metaKey || click.ctrlKey || click.shiftKey || click.altKey)
    return undefined
  const { href, target, download } = a
  if (!href || !/^\/(?![/\\])/.test(href)) return undefined
  if ((target && target !== '_self') || download) return undefined
  const path = href.split(/[?#]/)[0]
  if (path.split('/').pop()?.includes('.')) return undefined
  return href
}

/** Installs the delegated click handler; returns a cleanup function. */
export function installContentLinks(router: AnyRouter) {
  const onClick = (event: MouseEvent) => {
    const a =
      event.target instanceof Element
        ? event.target.closest('[data-content] a')
        : null
    if (!a) return
    const href = routableHref(event, {
      href: a.getAttribute('href'),
      target: a.getAttribute('target'),
      download: a.hasAttribute('download'),
    })
    if (href === undefined) return
    event.preventDefault()
    void router.navigate({ href })
  }
  document.addEventListener('click', onClick)
  return () => document.removeEventListener('click', onClick)
}
